/* eslint-disable import/no-named-as-default */
import { Button, Dropdown, FlexBox, Text } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import isHotkey from 'is-hotkey';
import { useCallback } from 'react';
import * as React from 'react';
import { Range } from 'slate';
import {
  Editable,
  RenderElementProps,
  RenderLeafProps,
  useFocused,
  useSelected,
  useSlate,
  useSlateStatic,
} from 'slate-react';

import styles from './styles.module.scss';
import { toggleMark } from './utils/editorUtils';
import { ImageElement } from './utils/image';
import { getLinkText, LinkElement, removeLink } from './utils/link';

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
};

export type RichTextProps = {
  /** Class to apply to editor body */
  className?: string;
  /** Should render in readonly mode */
  readOnly: boolean;
  /** Placeholder text */
  placeholder: string;
  /** Ref to be attached to file input */
  fileInputRef: React.RefObject<HTMLInputElement>;
  /** Onchange handler for file input */
  fileInputOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

/** Renders the rich text editor and hidden file input */
export function RichText({
  className,
  readOnly,
  placeholder,
  fileInputRef,
  fileInputOnChange,
}: RichTextProps): JSX.Element {
  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useSlate();

  return (
    <>
      <Editable
        className={classNames(styles.editor, styles.bulletinStyles, className)}
        readOnly={readOnly}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder={placeholder}
        autoFocus
        onKeyDown={(event) => {
          for (const hotkey in HOTKEYS) {
            if (isHotkey(hotkey, event)) {
              event.preventDefault();
              const mark = HOTKEYS[hotkey];
              toggleMark(editor, mark);
            }
          }
        }}
      />

      <input
        className={styles.fileInput}
        tabIndex={-1}
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg"
        onChange={fileInputOnChange}
      />
    </>
  );
}

function assertUnreachableElement(element: never): never {
  const msg = `${element} is not handled in Element`;
  console.warn(msg);
  throw new Error(msg);
}

/** Render out the possible Elements */
function Element(props: RenderElementProps): JSX.Element {
  const { attributes, children, element } = props;

  switch (element.type) {
    case 'image':
      return (
        <Image attributes={attributes} element={element}>
          {children}
        </Image>
      );
    case 'heading-two':
      return <h2 {...attributes}>{children}</h2>;
    case 'link':
      return (
        <Link attributes={attributes} element={element}>
          {children}
        </Link>
      );
    case 'paragraph':
      return (
        <div className={styles.paragraph} {...attributes}>
          {children}
        </div>
      );
    default:
      assertUnreachableElement(element);
  }
}

/** Render the text nodes with all possible formatting options applied */
const Leaf = ({ attributes, children, leaf }: RenderLeafProps): JSX.Element => {
  if (leaf.bold) children = <strong>{children}</strong>;
  if (leaf.italic) children = <em>{children}</em>;
  if (leaf.underline) children = <u>{children}</u>;
  return <span {...attributes}>{children}</span>;
};

/** Render the 'image' element. Shows hightlight on selection. */
function Image({
  attributes,
  children,
  element,
}: RenderElementProps & { element: ImageElement }): JSX.Element {
  const selected = useSelected();
  const focused = useFocused();
  return (
    <div {...attributes}>
      <div contentEditable={false}>
        <img
          className={classNames({
            [styles.image]: true,
            [styles.focused]: selected && focused,
          })}
          src={element.src}
        />
      </div>
      {children}
    </div>
  );
}

/** Render 'link' element, show a popup on focus to view link or remove it */
function Link({
  attributes,
  children,
  element,
}: RenderElementProps & { element: LinkElement }): JSX.Element {
  const editor = useSlateStatic();
  const selected = useSelected();
  const focused = useFocused();
  // Display popup only when range is collapsed to prevent
  // showing different popups if multiple links are selected
  const isOpen =
    selected && focused && editor.selection
      ? Range.isCollapsed(editor.selection)
      : false;

  return (
    <>
      <a
        {...attributes}
        href={element.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.preventDefault()}
      >
        {children}
      </a>
      <div className={styles.linkDropdownContainer} contentEditable={false}>
        <Dropdown className={styles.linkDropdown} isOpen={isOpen} fixed>
          <FlexBox flexDirection="column" padding="300">
            <Text.Heading withMarginBottom>{getLinkText(element)}</Text.Heading>
            <a
              className={styles.linkPreview}
              href={element.href}
              rel="noopener noreferrer"
              target="_blank"
            >
              {element.href}
            </a>

            <Button
              className={styles.linkRemove}
              variant="danger"
              onClick={() => removeLink(editor)}
            >
              Remove
            </Button>
          </FlexBox>
        </Dropdown>
      </div>
    </>
  );
}
